import { createAccountController } from './account/accountController'
import { createPreferenceController } from './account/preferenceController'
import { createSolidStateBridge } from './account/solidStateBridge'
import { createStartupSyncIndicator } from './account/startupSyncIndicator'
import { createHistoryApi } from './account/historyApi'
import { createMeasurementsApi } from './account/measurementsApi'
import { createMeasurementsSync } from './account/measurementsSync'
import { createWeightsApi } from './account/weightsApi'
import { createWeightsSync } from './account/weightsSync'
import { createHabitsApi } from './account/habitsApi'
import { createHabitsSync } from './account/habitsSync'
import { createRemoteSyncRuntime } from './account/remoteSyncRuntime'
import {
   createSyncEngine,
   type SyncStateOptions,
   type SyncStateResult,
} from './account/syncEngine'
import { buildUserDataExportPayload } from './account/userDataExport'
import { createHistorySummaryController } from './history/historySummaryController'
import {
   buildHashedUserId as buildHashedUserIdValue,
   sanitizeSyncBase,
} from './account/syncIdentity'
import { APP_BUILD_INFO } from './buildInfo'
import { translate, setLocaleOverride, type Locale } from './i18n'
import {
   formatDuration,
   historyMonthsDisplayed as solidHistoryMonthsDisplayed,
} from './state/currentFastStore'
import {
   setWeightChartMode as setSolidWeightChartMode,
   weightChartMode as solidWeightChartMode,
   weightFilter as solidWeightFilter,
} from './state/weightStore'
import { accountState } from './state/accountStore'
import {
   closeShareOverlay,
   openShareOverlayWithDefaults,
} from './state/shareOverlayStore'
import {
   normalizeAppTab,
   setActiveTab as setActiveTabState,
   setTabMenuCollapsed,
} from './state/appUiStore'
import {
   applyRemoteMeasurements,
   clearMeasurements,
   getPendingMeasurements,
   markMeasurementsPushed,
   measurementEntries,
} from './state/measurementsStore'
import { applyUnitSystemFromLegacy } from './state/unitSystemStore'
import { DEFAULT_UNIT_SYSTEM, type UnitSystem } from './utils/units'
import {
   normalizeHabit,
   normalizeHabitsForStorage,
   setHabitsFromSync,
   type Habit,
} from './state/habitsStore'
import { setAccentTheme } from './state/accentThemeStore'
import type {
   AccountActionDetail,
   AppActionDetail,
   CurrentFastStateDetail,
   LegacyRuntimeHandlers,
} from './runtimeBridge'
import type { AppTab } from './state/appUiStore'
import type { WeightEntry, WeightFilterKey } from './state/weightStore'
import {
   STORAGE_KEYS,
   loadStoredCurrentFast,
   loadStoredDisplayMode,
   loadStoredFastHistory,
   loadStoredHabits,
   loadStoredShowWeightTarget,
   loadStoredSyncDebugInfo,
   loadStoredUserId,
   loadStoredUserProfile,
   loadStoredWeightChartMode,
   loadStoredWeightHistory,
   persistDisplayMode,
   persistShowWeightTarget,
   persistStoredUserId,
   persistSyncDebugInfo,
   persistWeightChartMode,
   removeLocalAccountData,
   sanitizeLastPersistMeta,
   saveAppState,
   type SyncDebugInfo,
} from './storage/localPersistence'
import { triggerJsonDownload } from './utils/download'
import {
   canShowNotification,
   showFastCompleteNotification,
} from './utils/fastCompleteNotification'
import {
   DISPLAY_MODE_ELAPSED,
   DISPLAY_MODE_REMAINING,
   dedupeFastHistoryEntries,
   deserializeFastEntry,
   ensureCurrentFastDurationUpdatedAt,
   normalizeDisplayModePreference,
   prepareFastEntryForPersistence,
   sortFastEntriesDescending,
   type FastEntry,
} from './utils/fastEntry'
import { parseTimestampToNumber } from './utils/time'
import { deserializeWeightEntry } from './utils/weightEntry'

declare global {
   interface Window {
      FASTING_SYNC_API_BASE?: string
   }
}

type UserProfile = {
   heightCm: number | null
   targetWeightKg: number | null
   targetMonthlyRunEffort: number | null
   preferredLocale: Locale | null
   unitSystem: UnitSystem
}

type HistoryRemoteState = {
   monthsLoaded: number
   loadingPromise: Promise<void> | null
   error: unknown
   lastFetch: number
}

type SyncKeyOptions = {
   loadRemote?: boolean
   pushLocal?: boolean
   action?: string
}

/**
 * Registers the service worker when the app is served over HTTP(S).
 */
function registerServiceWorker() {
   const { protocol } = window.location
   if (
      !('serviceWorker' in navigator) ||
      (protocol !== 'https:' && protocol !== 'http:')
   ) {
      return
   }
   navigator.serviceWorker
      .register('/sw.js')
      .then(reg => console.log('Service Worker registered', reg))
      .catch(err => console.error('Service Worker registration failed', err))
}

/**
 * Deserializes a list of fast entries from a Solid store payload.
 *
 * @param entries - The raw entries.
 * @returns The valid entries, deduplicated and newest first.
 */
function parseFastHistoryPayload(entries: unknown) {
   const parsed =
      Array.isArray(entries) ?
         entries
            .map(entry => deserializeFastEntry(entry))
            .filter((entry): entry is FastEntry => entry !== null)
      :  []
   return dedupeFastHistoryEntries(parsed)
}

/**
 * Deserializes a list of weight entries from a Solid store payload.
 *
 * @param entries - The raw entries.
 * @returns The valid entries, newest first.
 */
function parseWeightHistoryPayload(entries: unknown) {
   return Array.isArray(entries) ?
         entries
            .map(entry => deserializeWeightEntry(entry))
            .filter((entry): entry is WeightEntry => entry !== null)
            .sort((a, b) => b.timestamp - a.timestamp)
      :  []
}

/**
 * Routes an account action from the Solid UI to the account controller.
 *
 * @param accountController - The account controller.
 * @param detail - The account action detail.
 */
function routeAccountAction(
   accountController: ReturnType<typeof createAccountController>,
   detail: AccountActionDetail,
) {
   switch (detail?.action) {
      case 'create-sync-key':
         accountController.handleCreateSyncKey().catch(error => {
            console.error('Failed to create sync key', error)
         })
         break
      case 'enter-sync-key':
         accountController.handleEnterSyncKey(detail.syncKey.trim()).catch(error => {
            console.error('Failed to enter sync key', error)
         })
         break
      case 'create-qr-code':
         accountController.handleCreateQrCode()
         break
      case 'download-data':
         accountController.handleAccountDownload()
         break
      case 'delete-account':
         accountController.handleAccountDelete(STORAGE_KEYS.debugState).catch(error => {
            console.error('Failed to delete account', error)
         })
         break
      case 'save-profile':
         accountController.handleAccountFormSubmit(detail).catch(error => {
            console.error('Failed to save profile', error)
         })
         break
      default:
         break
   }
}

type AppActionTargets = {
   syncEngine: Pick<ReturnType<typeof createSyncEngine>, 'triggerImmediateSyncRetry'>
   historySummaryController: Pick<
      ReturnType<typeof createHistorySummaryController>,
      'selectPeriod' | 'freezeFor'
   >
   applyLocalePreference: ReturnType<
      typeof createPreferenceController
   >['applyLocalePreference']
   applyUnitSystemPreference: ReturnType<
      typeof createPreferenceController
   >['applyUnitSystemPreference']
}

/**
 * Routes an app-level action (retry sync, locale, history period, freeze summary).
 *
 * @param targets - The controllers the actions apply to.
 * @param detail - The app action detail.
 */
function routeAppAction(targets: AppActionTargets, detail: AppActionDetail) {
   const { syncEngine, historySummaryController } = targets
   switch (detail?.action) {
      case 'retry-sync-now':
         syncEngine.triggerImmediateSyncRetry()
         break
      case 'select-locale':
         targets.applyLocalePreference(detail.locale, {
            persist: true,
            sync: true,
            markChange: true,
         })
         break
      case 'select-unit-system':
         targets.applyUnitSystemPreference(detail.unitSystem)
         break
      case 'select-history-period':
         historySummaryController.selectPeriod(detail.periodKey)
         break
      case 'freeze-history-summary':
         historySummaryController.freezeFor(600)
         break
      default:
         break
   }
}

/**
 * Bootstraps the legacy (non-Solid) app runtime: wires state, controllers, sync, and event handlers.
 *
 * @returns The handler set the Solid runtime bridge dispatches into.
 */
export function initializeFastingApp(): LegacyRuntimeHandlers {
   const API_VERSION = 'v1'
   const DEFAULT_SYNC_BASE = 'https://ieiunus.obscurum.cc'
   const syncApiBase =
      typeof window.FASTING_SYNC_API_BASE === 'string' ?
         window.FASTING_SYNC_API_BASE.trim()
      :  DEFAULT_SYNC_BASE
   const normalizedSyncBase = sanitizeSyncBase(syncApiBase)
   const SYNC_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/sync`
      :  `/${API_VERSION}/sync`
   const HISTORY_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/history`
      :  `/${API_VERSION}/history`
   const HISTORY_SUMMARY_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/history/summary`
      :  `/${API_VERSION}/history/summary`
   const MEASUREMENTS_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/measurements`
      :  `/${API_VERSION}/measurements`
   const HABITS_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/habits`
      :  `/${API_VERSION}/habits`
   const WEIGHTS_ENDPOINT =
      normalizedSyncBase ?
         `${normalizedSyncBase}/${API_VERSION}/weights`
      :  `/${API_VERSION}/weights`
   const HASH_ALGORITHM = 'SHA-256'
   const SYNC_RETRY_INTERVAL_MS = 60 * 1000
   const ADJUST_DURATION_SYNC_DEBOUNCE_MS = 1500
   const LIGHT_SYNC_INTERVAL_MS = 15 * 1000
   const MAX_SYNC_KEEPALIVE_BODY_BYTES = 60 * 1024
   const APP_SHARE_BASE_URL = 'https://fasting-timer.pages.dev/'

   let showWeightTarget = true
   const WEIGHT_FILTER_CONFIG = {
      week: { key: 'week', labelKey: 'Week', days: 7 },
      month: { key: 'month', labelKey: 'Month', days: 30 },
      quarter: { key: 'quarter', labelKey: '3 Months', days: 90 },
      year: { key: 'year', labelKey: 'Year', days: 365 },
      max: { key: 'max', labelKey: 'Max', days: null },
   } as const

   const DEFAULT_WEIGHT_FILTER_KEY: WeightFilterKey = 'quarter'

   let currentFast: FastEntry | null = null
   let fastHistory: FastEntry[] = []
   let weightHistory: WeightEntry[] = []
   let habits: Habit[] = []
   let focusSyncInProgress = false
   const userProfile: UserProfile = {
      heightCm: null,
      targetWeightKg: null,
      targetMonthlyRunEffort: null,
      preferredLocale: null,
      unitSystem: DEFAULT_UNIT_SYSTEM,
   }
   let timerInterval: number | null = null
   let adjustDurationSyncTimerId: number | null = null
   let latestLocalDurationChangeAt = 0
   const userIdentity = {
      userId: '',
   }
   const syncDebugInfo: SyncDebugInfo = {
      lastPersist: null,
      clientBuildInfo: APP_BUILD_INFO,
      serverBuildInfo: null,
   }
   const urlParams = new URLSearchParams(window.location.search)
   const urlUserIdParam = urlParams.get('userId')?.trim() ?? ''
   let activeTab: AppTab = normalizeAppTab(urlParams.get('tab'))
   let stateVersion = 0
   let weightFilter: WeightFilterKey = DEFAULT_WEIGHT_FILTER_KEY
   let fastCompletionNotificationInFlight = false
   let displayModePreference: 'elapsed' | 'remaining' = DISPLAY_MODE_ELAPSED
   let showTimeRemaining = false
   let lightweightSyncTimerId: number | null = null
   let lightweightSyncInFlight = false
   let remoteLoadInProgress = false
   let initialRemoteHydrationPending = false
   let foregroundRemoteRefreshPending = false
   let historyMonthsDisplayed = 1
   const historyRemoteState: HistoryRemoteState = {
      monthsLoaded: 0,
      loadingPromise: null,
      error: null,
      lastFetch: 0,
   }
   let isWideScreenViewport = window.innerWidth >= 640

   const solidBridge = createSolidStateBridge({
      getCurrentFast: () => currentFast,
      getFastHistory: () => fastHistory,
      getWeightHistory: () => weightHistory,
      getHabits: () => habits,
      getUserProfile: () => userProfile,
      getUserId: () => userIdentity.userId,
      canUseRemoteSync: () => canUseRemoteSync(),
      getHistoryRemoteState: () => historyRemoteState,
      getDisplayModePreference: () => displayModePreference,
      shouldShowStartupSyncLoading: () => shouldShowStartupSyncLoading(),
      getShowWeightTarget: () => showWeightTarget,
      getRemoteLoadInProgress: () => remoteLoadInProgress,
      getSyncServerUpdatedAt: () => syncDebugInfo.lastPersist?.serverUpdatedAt,
      getSyncFailureState: () => syncEngine.getFailureState(),
   })
   const {
      syncAccountStateToSolid,
      syncCurrentFastStateToSolid,
      syncHabitsStateToSolid,
      syncHistoryRemoteStateToSolid,
      syncHistoryStateToSolid,
      syncWeightStateToSolid,
   } = solidBridge
   const startupSyncIndicator = createStartupSyncIndicator({
      shouldShow: () => shouldShowStartupSyncLoading(),
      render: syncCurrentFastStateToSolid,
   })
   const {
      applyLocalePreference,
      applyUnitSystemPreference,
      initializeLocalePreference,
   } = createPreferenceController({
      userProfile,
      markStateChanged: () => markStateChanged(),
      saveState: () => saveState(),
      syncState: action => syncState(action),
      refreshLocaleDependentUi: () => refreshLocaleDependentUi(),
      syncAccountStateToSolid,
   })

   /**
    * Returns whether the startup/foreground cloud-sync loading state should be shown.
    *
    * @returns True while an initial or focus refresh is pending.
    */
   function shouldShowStartupSyncLoading() {
      return (
         (initialRemoteHydrationPending || foregroundRemoteRefreshPending) &&
         Boolean(userIdentity.userId) &&
         canUseRemoteSync()
      )
   }

   /**
    * Syncs the active-tab label into the Solid store.
    *
    * @param tabKey - The tab to mark active (defaults to the current tab).
    */
   function updateActiveTabLabel(tabKey: AppTab = activeTab) {
      setActiveTabState(tabKey)
   }

   /**
    * Collapses or expands the tab menu based on viewport width.
    */
   function syncTabMenuForViewport() {
      setTabMenuCollapsed(!isWideScreenViewport)
      updateActiveTabLabel()
   }

   /**
    * Bumps the local state version to invalidate in-flight sync snapshots.
    */
   function markStateChanged() {
      stateVersion += 1
   }

   /**
    * Re-renders all locale-dependent UI after a language change.
    */
   function refreshLocaleDependentUi() {
      updateUI()
      syncHistoryStateToSolid()
      syncWeightStateToSolid()
      syncAccountStateToSolid()
   }

   /**
    * Sorts the local fast history newest first.
    */
   function sortFastHistoryDescending() {
      sortFastEntriesDescending(fastHistory)
   }

   /**
    * Persists the sync debug info to storage.
    */
   function persistDebugInfo() {
      persistSyncDebugInfo(syncDebugInfo)
   }

   /**
    * Builds a history-shaped snapshot of the active fast up to now.
    *
    * @returns The snapshot, or null when no fast is active.
    */
   function getCurrentFastSnapshot(): FastEntry | null {
      if (!currentFast || typeof currentFast.startTime !== 'number') {
         return null
      }

      const now = Date.now()

      return {
         startTime: currentFast.startTime,
         endTime: now,
         duration: currentFast.duration,
         intendedDuration: currentFast.duration,
         durationUpdatedAt: currentFast.durationUpdatedAt,
         notifiedCompletion: currentFast.notifiedCompletion,
         displayMode: currentFast.displayMode,
         isCurrent: true,
      }
   }

   /**
    * Runs the full app startup: service worker, state load, identity, and first render.
    */
   function initializeApp() {
      setAccentTheme('blue')
      registerServiceWorker()
      loadState()
      initializeLocalePreference()

      if (urlUserIdParam) {
         const savedUserId = loadStoredUserId()
         userIdentity.userId = urlUserIdParam
         saveIdentity()

         if (!savedUserId || savedUserId !== urlUserIdParam) {
            resetLocalState()
         }
      }

      loadIdentity()
      initialRemoteHydrationPending = Boolean(userIdentity.userId && canUseRemoteSync())
      syncTabMenuForViewport()
      updateUI()
      syncHistoryStateToSolid()
      syncWeightStateToSolid()
      syncAccountStateToSolid()
      closeShareOverlay()
      switchTab(activeTab, { suppressHistoryUpdate: true })
      if (userIdentity.userId) {
         loadRemoteState()
      }
   }

   /**
    * Loads all persisted state (fast, history, weights, habits, profile, preferences) into memory.
    */
   function loadState() {
      applyDisplayModePreference(loadStoredDisplayMode() ?? DISPLAY_MODE_ELAPSED, {
         persistLocal: false,
         updateCurrentFast: false,
      })

      currentFast = loadStoredCurrentFast()
      if (currentFast) {
         latestLocalDurationChangeAt = Math.max(
            latestLocalDurationChangeAt,
            currentFast.durationUpdatedAt,
         )
         currentFast.displayMode = getDisplayModePreference()
      }

      fastHistory = loadStoredFastHistory()
      weightHistory = loadStoredWeightHistory()
      weightsSync.init()
      habits = loadStoredHabits()
      habitsSync.init()

      const storedProfile = loadStoredUserProfile()
      if (storedProfile) {
         userProfile.heightCm = storedProfile.heightCm
         userProfile.targetWeightKg = storedProfile.targetWeightKg
         userProfile.targetMonthlyRunEffort = storedProfile.targetMonthlyRunEffort
         userProfile.preferredLocale = storedProfile.preferredLocale
         userProfile.unitSystem = storedProfile.unitSystem
      }
      applyUnitSystemFromLegacy(userProfile.unitSystem)

      const savedShowTarget = loadStoredShowWeightTarget()
      if (savedShowTarget !== null) {
         showWeightTarget = savedShowTarget
      }
      const savedChartMode = loadStoredWeightChartMode()
      if (savedChartMode) {
         setSolidWeightChartMode(savedChartMode, { emit: false })
      }

      const storedDebugInfo = loadStoredSyncDebugInfo()
      syncDebugInfo.lastPersist = storedDebugInfo.lastPersist
      syncDebugInfo.clientBuildInfo = APP_BUILD_INFO
      syncDebugInfo.serverBuildInfo = storedDebugInfo.serverBuildInfo

      historyRemoteState.monthsLoaded = 0
      historyRemoteState.loadingPromise = null
      historyRemoteState.error = null
      historyRemoteState.lastFetch = 0

      fastCompletionNotificationInFlight = false
   }

   /**
    * Persists the full in-memory state and sync debug info.
    */
   function saveState() {
      saveAppState({
         currentFast,
         fastHistory,
         weightHistory,
         habits,
         userProfile,
      })
      persistDebugInfo()
   }

   /**
    * Persists the current user id (hashed sync key).
    */
   function saveIdentity() {
      persistStoredUserId(userIdentity.userId || null)
   }

   /**
    * Sets the active sync key, resetting sync state and optionally loading or pushing data.
    *
    * @param newKey - The new sync key.
    * @param options - Whether to load remote, push local, and the sync action label.
    * @returns The outcome (ok, remoteSynced, remoteEnabled).
    */
   async function setSyncKey(newKey: string, options: SyncKeyOptions = {}) {
      const trimmed = typeof newKey === 'string' ? newKey.trim() : ''
      if (!trimmed) {
         return {
            ok: false,
            remoteSynced: false,
            remoteEnabled: canUseRemoteSync(),
         }
      }

      userIdentity.userId = trimmed
      saveIdentity()

      historyRemoteState.monthsLoaded = 0
      historyRemoteState.loadingPromise = null
      historyRemoteState.error = null
      historyRemoteState.lastFetch = 0
      historySummaryController.clearCache()
      clearSyncFailureState()
      remoteSyncRuntime.stopLightweightSyncLoop()
      remoteLoadInProgress = false

      syncDebugInfo.lastPersist = null
      persistDebugInfo()
      saveState()
      syncAccountStateToSolid()

      const remoteEnabled = canUseRemoteSync()
      let remoteSynced = false
      let syncOk = true

      if (options.loadRemote === true && remoteEnabled) {
         initialRemoteHydrationPending = true
         updateUI()
         await loadRemoteState()
         remoteSynced = true
      } else if (options.pushLocal !== false && remoteEnabled) {
         const response = await syncState(options.action || 'sync-key-update')
         syncOk = response !== false
         remoteSynced = true
         syncDeltaCollections()
      }

      if (remoteEnabled) {
         remoteSyncRuntime.startLightweightSyncLoop()
      }

      return { ok: syncOk, remoteSynced, remoteEnabled }
   }

   /**
    * Hashes the current user id for use as the server-side account id.
    *
    * @returns The hashed user id.
    */
   async function buildHashedUserId() {
      return buildHashedUserIdValue(userIdentity.userId, HASH_ALGORITHM)
   }

   /**
    * Returns whether remote sync is possible (endpoint configured and not on file://).
    *
    * @returns True when remote sync may be used.
    */
   function canUseRemoteSync() {
      if (!SYNC_ENDPOINT) {
         return false
      }

      if (window.location.protocol === 'file:') {
         return false
      }

      return true
   }

   /**
    * Clears all in-memory and derived state back to a signed-out baseline.
    */
   function resetLocalState() {
      currentFast = null
      fastHistory = []
      weightHistory = []
      habits = []
      userProfile.heightCm = null
      userProfile.targetWeightKg = null
      userProfile.targetMonthlyRunEffort = null
      userProfile.preferredLocale = null
      userProfile.unitSystem = DEFAULT_UNIT_SYSTEM
      applyUnitSystemFromLegacy(userProfile.unitSystem)
      clearMeasurements()
      measurementsSync.resetCursor()
      weightsSync.reset()
      weightsSync.init()
      habitsSync.reset()
      habitsSync.init()
      setLocaleOverride(null)
      cancelAdjustDurationSync()
      syncDebugInfo.lastPersist = null
      showTimeRemaining = displayModePreference === DISPLAY_MODE_REMAINING
      historyRemoteState.monthsLoaded = 0
      historyRemoteState.loadingPromise = null
      historyRemoteState.error = null
      historyRemoteState.lastFetch = 0
      historySummaryController.clearCache()
      clearSyncFailureState()
      markStateChanged()
      saveState()
      updateUI()
      syncHistoryStateToSolid()
      syncWeightStateToSolid()
      syncHabitsStateToSolid()
      syncAccountStateToSolid()
      closeShareOverlay()
      switchTab(activeTab)
   }

   /**
    * Builds the serialized state snapshot sent to the server on sync.
    *
    * @returns The sync request snapshot.
    */
   function createStateSnapshot() {
      const history = Array.isArray(fastHistory) ? fastHistory : []
      const dedupedHistory = dedupeFastHistoryEntries(history)
      const snapshotCurrentFast =
         currentFast ? prepareFastEntryForPersistence(currentFast) : null
      if (snapshotCurrentFast && typeof snapshotCurrentFast === 'object') {
         snapshotCurrentFast.displayMode = getDisplayModePreference()
      }

      const snapshotHistory = dedupedHistory
         .map(entry => prepareFastEntryForPersistence(entry))
         .filter(entry => entry !== null)

      return {
         currentFast: snapshotCurrentFast,
         fastHistory: snapshotHistory,
         heightCm: typeof userProfile.heightCm === 'number' ? userProfile.heightCm : null,
         targetWeightKg:
            typeof userProfile.targetWeightKg === 'number' ?
               userProfile.targetWeightKg
            :  null,
         targetMonthlyRunEffort:
            typeof userProfile.targetMonthlyRunEffort === 'number' ?
               userProfile.targetMonthlyRunEffort
            :  null,
         preferredLocale:
            typeof userProfile.preferredLocale === 'string' ?
               userProfile.preferredLocale
            :  null,
         unitSystem: userProfile.unitSystem,
         weightChartMode: solidWeightChartMode(),
         timerDisplayMode: getDisplayModePreference(),
         showTimeRemaining,
      }
   }

   /**
    * Builds the full user-data export payload for download.
    *
    * @returns The export payload.
    */
   function buildUserDataExport() {
      return buildUserDataExportPayload({
         snapshot: createStateSnapshot(),
         fastHistory,
         weightHistory,
         habits,
         measurements: measurementEntries().filter(entry => entry.deletedAt === null),
         userProfile,
         currentFast,
         lastPersistMeta: syncDebugInfo.lastPersist,
         userId: userIdentity.userId,
         stateVersion,
         userSettings: {
            weightChartMode: solidWeightChartMode(),
            weightFilter,
            showTimeRemaining,
            historySummaryPeriod: historySummaryController.getPeriod(),
            timerDisplayMode: getDisplayModePreference(),
         },
      })
   }

   const accountController = createAccountController({
      appShareBaseUrl: APP_SHARE_BASE_URL,
      buildUserDataExport,
      canUseRemoteSync,
      closeQrOverlay: closeShareOverlay,
      /**
       * Returns the current account form input state.
       *
       * @returns The account form state.
       */
      getAccountFormState: () => accountState(),
      /**
       * Returns the current in-progress fast, or null.
       *
       * @returns The current fast.
       */
      getCurrentFast: () => currentFast,
      /**
       * Returns the number of local fast-history entries.
       *
       * @returns The history length.
       */
      getFastHistoryLength: () => fastHistory.length,
      /**
       * Returns the last server-updated timestamp from sync metadata.
       *
       * @returns The server timestamp, or undefined.
       */
      getSyncServerUpdatedAt: () =>
         syncDebugInfo.lastPersist && syncDebugInfo.lastPersist.serverUpdatedAt,
      /**
       * Returns the current user id (sync key).
       *
       * @returns The user id.
       */
      getUserId: () => userIdentity.userId,
      /**
       * Returns the in-memory user profile.
       *
       * @returns The user profile.
       */
      getUserProfile: () => userProfile,
      /**
       * Returns the number of local weight entries.
       *
       * @returns The weight-history length.
       */
      getWeightHistoryLength: () => weightHistory.length,
      markStateChanged,
      openQrOverlay: options =>
         openShareOverlayWithDefaults(APP_SHARE_BASE_URL, options, translate),
      parseTimestampToNumber,
      removeLocalAccountStorage: removeLocalAccountData,
      resetLocalState,
      saveIdentity,
      saveState,
      /**
       * Sets whether a fast-completion notification is in flight.
       *
       * @param value - The new flag value.
       */
      setFastCompletionNotificationInFlight: value => {
         fastCompletionNotificationInFlight = value
      },
      setSyncKeyState: setSyncKey,
      /**
       * Sets the current user id.
       *
       * @param value - The new user id.
       */
      setUserId: value => {
         userIdentity.userId = value
      },
      syncAccountStateToSolid,
      syncState,
      syncWeightStateToSolid,
      translate,
      triggerJsonDownload,
      userProfile,
   })

   const remoteSyncRuntime = createRemoteSyncRuntime({
      applyLocalePreference,
      applyUnitSystemPreference,
      buildHashedUserId,
      buildRequestSnapshot: createStateSnapshot,
      canUseRemoteSync,
      clearSyncFailureState,
      /**
       * Returns the current local state version.
       *
       * @returns The state version.
       */
      currentStateVersion: () => stateVersion,
      deserializeFastEntry,
      ensureCurrentFastDurationUpdatedAt,
      /**
       * Returns the active tab.
       *
       * @returns The active tab.
       */
      getActiveTab: () => activeTab,
      /**
       * Returns the current in-progress fast, or null.
       *
       * @returns The current fast.
       */
      getCurrentFast: () => currentFast,
      /**
       * Returns the local fast history.
       *
       * @returns The fast history.
       */
      getFastHistory: () => fastHistory,
      /**
       * Returns the remote-history load state.
       *
       * @returns The remote-history state.
       */
      getHistoryRemoteState: () => historyRemoteState,
      /**
       * Returns the timestamp of the latest local duration change.
       *
       * @returns The timestamp in epoch ms.
       */
      getLatestLocalDurationChangeAt: () => latestLocalDurationChangeAt,
      /**
       * Returns whether a lightweight sync is in flight.
       *
       * @returns True when a lightweight sync is running.
       */
      getLightweightSyncInFlight: () => lightweightSyncInFlight,
      /**
       * Returns the lightweight-sync interval id, or null.
       *
       * @returns The timer id, or null.
       */
      getLightweightSyncTimerId: () => lightweightSyncTimerId,
      /**
       * Returns whether a remote state load is in progress.
       *
       * @returns True during a remote load.
       */
      getRemoteLoadInProgress: () => remoteLoadInProgress,
      /**
       * Returns the sync debug info.
       *
       * @returns The sync debug info.
       */
      getSyncDebugInfo: () => syncDebugInfo,
      /**
       * Returns the target user id for sync.
       *
       * @returns The user id.
       */
      getTargetUserId: () => userIdentity.userId,
      /**
       * Returns the mutable user-identity object.
       *
       * @returns The user identity.
       */
      getUserIdentity: () => userIdentity,
      /**
       * Returns the in-memory user profile.
       *
       * @returns The user profile.
       */
      getUserProfile: () => userProfile,
      historyEndpoint: HISTORY_ENDPOINT,
      /**
       * Clears the history-summary controller's cache.
       */
      historySummaryCacheClear: () => historySummaryController.clearCache(),
      /**
       * Returns whether the initial remote hydration is still pending.
       *
       * @returns True while initial hydration is pending.
       */
      initialRemoteHydrationPending: () => initialRemoteHydrationPending,
      lightSyncIntervalMs: LIGHT_SYNC_INTERVAL_MS,
      markStateChanged,
      normalizeDisplayModePreference,
      parseTimestampToNumber,
      persistDebugInfo,
      prepareFastEntryForPersistence,
      sanitizeLastPersistMeta,
      saveState,
      /**
       * Sets the current fast.
       *
       * @param value - The new current fast, or null.
       */
      setCurrentFast: value => {
         currentFast = value
      },
      /**
       * Sets whether a fast-completion notification is in flight.
       *
       * @param value - The new flag value.
       */
      setFastCompletionNotificationInFlight: value => {
         fastCompletionNotificationInFlight = value
      },
      /**
       * Replaces the fast history (de-duplicated).
       *
       * @param value - The new fast entries.
       */
      setFastHistory: value => {
         fastHistory = dedupeFastHistoryEntries(value)
      },
      /**
       * Mutates the remote-history state via an updater.
       *
       * @param updater - Receives the remote-history state to mutate.
       */
      setHistoryRemoteState: updater => {
         updater(historyRemoteState)
      },
      /**
       * Sets the initial-hydration-pending flag.
       *
       * @param value - The new flag value.
       */
      setInitialRemoteHydrationPending: value => {
         initialRemoteHydrationPending = value
      },
      /**
       * Sets the latest local duration-change timestamp.
       *
       * @param value - The timestamp in epoch ms.
       */
      setLatestLocalDurationChangeAt: value => {
         latestLocalDurationChangeAt = value
      },
      /**
       * Sets whether a lightweight sync is in flight.
       *
       * @param value - The new flag value.
       */
      setLightweightSyncInFlight: value => {
         lightweightSyncInFlight = value
      },
      /**
       * Sets the lightweight-sync interval id.
       *
       * @param value - The timer id, or null.
       */
      setLightweightSyncTimerId: value => {
         lightweightSyncTimerId = value
      },
      /**
       * Sets the remote-load-in-progress flag, refreshing account UI on change.
       *
       * @param value - The new flag value.
       */
      setRemoteLoadInProgress: value => {
         if (remoteLoadInProgress === value) {
            return
         }
         remoteLoadInProgress = value
         syncAccountStateToSolid()
      },
      setSolidWeightChartMode: setSolidWeightChartMode,
      sortFastHistoryDescending,
      switchTab,
      syncAccountStateToSolid,
      syncCurrentFastStateToSolid,
      syncHistoryStateToSolid,
      syncHabitsStateToSolid,
      /**
       * Pulls measurement and weight changes on the lightweight tick (hoisted;
       * the sync runtimes it uses are created further down).
       */
      syncDeltaCollections: options => syncDeltaCollections(options),
      syncState,
      syncWeightStateToSolid,
      syncEndpoint: SYNC_ENDPOINT,
      updateHistorySummary: options => historySummaryController.update(options),
      updateUI,
      upsertRemoteHistoryEntry: entry => historyApi.upsertRemoteHistoryEntry(entry),
   })

   /**
    * Resolves the hashed user id for a sync request, falling back to the raw id.
    *
    * @returns The user id to send.
    */
   async function resolveSyncUserId() {
      const hashedId = await buildHashedUserId()
      return hashedId || userIdentity.userId
   }

   const syncEngine = createSyncEngine({
      syncEndpoint: SYNC_ENDPOINT,
      syncRetryIntervalMs: SYNC_RETRY_INTERVAL_MS,
      maxKeepaliveBodyBytes: MAX_SYNC_KEEPALIVE_BODY_BYTES,
      buildRequestSnapshot: createStateSnapshot,
      canUseRemoteSync,
      /**
       * Returns the current user id (sync key).
       *
       * @returns The user id.
       */
      getUserId: () => userIdentity.userId,
      resolveSyncUserId,
      /**
       * Returns the current local state version.
       *
       * @returns The state version.
       */
      getStateVersion: () => stateVersion,
      getDisplayModePreference,
      /**
       * Returns the in-memory user profile.
       *
       * @returns The user profile.
       */
      getUserProfile: () => userProfile,
      /**
       * Returns the sync debug info.
       *
       * @returns The sync debug info.
       */
      getSyncDebugInfo: () => syncDebugInfo,
      persistDebugInfo,
      loadRemoteState,
      /**
       * Restores a current fast into memory and the Solid store.
       *
       * @param entry - The fast entry to restore.
       */
      restoreCurrentFast: entry => {
         currentFast = entry
         saveState()
         syncCurrentFastStateToSolid()
      },
      syncAccountStateToSolid,
   })

   const historyApi = createHistoryApi({
      historyEndpoint: HISTORY_ENDPOINT,
      historySummaryEndpoint: HISTORY_SUMMARY_ENDPOINT,
      canUseRemoteSync,
      /**
       * Returns the current user id (sync key).
       *
       * @returns The user id.
       */
      getUserId: () => userIdentity.userId,
      resolveSyncUserId,
   })

   const measurementsSync = createMeasurementsSync({
      api: createMeasurementsApi({
         measurementsEndpoint: MEASUREMENTS_ENDPOINT,
         canUseRemoteSync,
         /**
          * Returns the current user id (sync key).
          *
          * @returns The user id.
          */
         getUserId: () => userIdentity.userId,
         resolveSyncUserId,
      }),
      getPendingEntries: getPendingMeasurements,
      applyRemote: applyRemoteMeasurements,
      markPushed: markMeasurementsPushed,
   })

   /**
    * Runs a measurements push/pull cycle, logging failures instead of throwing so a
    * measurements outage never blocks the main sync flow.
    */
   function syncMeasurements() {
      measurementsSync.sync().catch(error => {
         console.error('Failed to sync measurements', error)
      })
   }

   const weightsSync = createWeightsSync({
      api: createWeightsApi({
         weightsEndpoint: WEIGHTS_ENDPOINT,
         canUseRemoteSync,
         /**
          * Returns the current user id (sync key).
          *
          * @returns The user id.
          */
         getUserId: () => userIdentity.userId,
         resolveSyncUserId,
      }),
      getLocalEntries: () => weightHistory,
      /**
       * Applies weights merged in from the server and persists them locally.
       *
       * @param entries - The merged weight entries, newest first.
       */
      setLocalEntries: entries => {
         weightHistory = entries
         saveState()
         syncWeightStateToSolid()
      },
      onServerStamp: adoptWeightsServerStamp,
   })

   /**
    * Moves the local `/v1/sync` conflict stamp forward after this device's own
    * weight write, but only when nothing else changed on the server in between;
    * otherwise the next sync's 409 correctly triggers a reload.
    *
    * @param change - The new stamp and the one the write replaced.
    */
   function adoptWeightsServerStamp(change: {
      serverUpdatedAt: string
      previousServerUpdatedAt: string | null
   }) {
      const lastPersist = syncDebugInfo.lastPersist
      const previousMs = parseTimestampToNumber(change.previousServerUpdatedAt)
      const nextMs = parseTimestampToNumber(change.serverUpdatedAt)
      if (
         lastPersist &&
         previousMs !== null &&
         nextMs !== null &&
         lastPersist.serverUpdatedAt === previousMs
      ) {
         lastPersist.serverUpdatedAt = nextMs
         saveState()
      }
   }

   /**
    * Runs a weights push/pull cycle, logging failures instead of throwing so a
    * weights outage never blocks the main sync flow.
    */
   function syncWeights() {
      weightsSync.sync().catch(error => {
         console.error('Failed to sync weights', error)
      })
   }

   const habitsSync = createHabitsSync({
      api: createHabitsApi({
         habitsEndpoint: HABITS_ENDPOINT,
         canUseRemoteSync,
         /**
          * Returns the current user id (sync key).
          *
          * @returns The user id.
          */
         getUserId: () => userIdentity.userId,
         resolveSyncUserId,
      }),
      getLocalHabits: () => habits,
      toLocalHabit: normalizeHabit,
      /**
       * Applies habits merged in from the server and persists them locally.
       *
       * @param entries - The merged habits.
       * @param deleted - Habits deleted elsewhere, by deletion time.
       */
      setLocalHabits: (entries, deleted) => {
         habits = setHabitsFromSync(entries, deleted)
         saveState()
      },
   })

   /**
    * Runs a habits push/pull cycle, logging failures instead of throwing so a
    * habits outage never blocks the main sync flow.
    */
   function syncHabits() {
      habitsSync.sync().catch(error => {
         console.error('Failed to sync habits', error)
      })
   }

   /**
    * Syncs the collections that use per-entry delta endpoints; with `all: false`
    * only those with a first pull or unpushed edits.
    *
    * @param options - `all: false` skips collections with nothing local to do.
    */
   function syncDeltaCollections(options: { all: boolean } = { all: true }) {
      if (options.all || measurementsSync.hasLocalWork()) {
         syncMeasurements()
      }
      if (options.all || weightsSync.hasLocalWork()) {
         syncWeights()
      }
      if (options.all || habitsSync.hasLocalWork()) {
         syncHabits()
      }
   }

   const historySummaryController = createHistorySummaryController({
      /**
       * Returns the local fast history.
       *
       * @returns The fast history.
       */
      getFastHistory: () => fastHistory,
      getCurrentFastSnapshot,
      /**
       * Returns whether the remote history summary can be fetched.
       *
       * @returns True when a user id is set and remote sync is usable.
       */
      canFetchRemote: () => Boolean(userIdentity.userId && canUseRemoteSync()),
      /**
       * Fetches the remote history summary for a period.
       *
       * @param period - The period key.
       * @returns The summary promise.
       */
      fetchRemoteSummary: period => historyApi.fetchRemoteHistorySummary(period),
   })

   /**
    * Clears the sync engine's failure state and banner.
    */
   function clearSyncFailureState() {
      syncEngine.clearSyncFailureState()
   }

   /**
    * Refreshes remote state when the window regains focus, guarding against overlap.
    */
   function requestRemoteRefreshOnFocus() {
      if (!userIdentity.userId || !canUseRemoteSync()) {
         return
      }
      if (focusSyncInProgress) {
         return
      }
      focusSyncInProgress = true
      foregroundRemoteRefreshPending = true
      startupSyncIndicator.show()
      loadRemoteState()
         .catch(error => {
            console.error('Failed to refresh state on focus', error)
         })
         .finally(() => {
            foregroundRemoteRefreshPending = false
            startupSyncIndicator.clear()
            focusSyncInProgress = false
         })
   }

   /**
    * Pushes local state to the server for an action via the sync engine.
    *
    * @param action - The action driving the sync.
    * @param syncOptions - Optional sync options.
    * @returns The sync result.
    */
   function syncState(
      action: string,
      syncOptions: SyncStateOptions = {},
   ): Promise<SyncStateResult> {
      return syncEngine.syncState(action, syncOptions)
   }

   /**
    * Loads and merges the full remote state via the remote-sync runtime.
    *
    * @param options - Optional load hints.
    * @returns The load result.
    */
   async function loadRemoteState(options: Record<string, unknown> = {}) {
      const result = await remoteSyncRuntime.loadRemoteState(options)
      syncDeltaCollections()
      return result
   }

   /**
    * Loads the stored user id into memory when none is set yet.
    */
   function loadIdentity() {
      const savedUserId = loadStoredUserId()

      if (!userIdentity.userId && savedUserId) {
         userIdentity.userId = savedUserId
      }
   }

   /**
    * Shows the fast-complete notification once, when permitted and not already sent.
    */
   async function triggerFastCompleteNotification() {
      if (
         !currentFast ||
         currentFast.notifiedCompletion ||
         fastCompletionNotificationInFlight ||
         !canShowNotification()
      ) {
         return
      }
      fastCompletionNotificationInFlight = true
      try {
         await showFastCompleteNotification(formatDuration(currentFast.duration || 0))
         currentFast.notifiedCompletion = true
         markStateChanged()
         saveState()
      } catch (error) {
         console.error('Failed to display completion notification', error)
      } finally {
         fastCompletionNotificationInFlight = false
      }
   }

   /**
    * Re-renders the history list and forces a summary refresh.
    */
   function refreshHistoryView() {
      syncHistoryStateToSolid()
      historySummaryController.update({ force: true }).catch(error => {
         console.error('Failed to refresh history summary', error)
      })
   }

   /**
    * Switches the active tab, refreshing its data and updating the URL.
    *
    * @param tab - The tab to activate.
    * @param options - Set `suppressHistoryUpdate` to skip the URL update.
    */
   function switchTab(
      tab: AppTab | undefined,
      options: { suppressHistoryUpdate?: boolean } = {},
   ) {
      const suppressHistoryUpdate = options.suppressHistoryUpdate === true
      const previousTab = activeTab
      const targetTab = normalizeAppTab(tab)

      if (targetTab === 'history' && previousTab !== 'history') {
         historyMonthsDisplayed = 1
      }

      if (targetTab === 'history') {
         refreshHistoryView()
         if (previousTab !== 'history' && userIdentity.userId && canUseRemoteSync()) {
            remoteSyncRuntime
               .ensureRemoteHistoryForMonths(historyMonthsDisplayed)
               .catch(error => {
                  console.error('Failed to ensure remote history', error)
               })
               .finally(refreshHistoryView)
         }
      }

      if (targetTab === 'measurements') {
         syncWeightStateToSolid()
      }

      if (targetTab === 'habits') {
         syncHabitsStateToSolid()
      }

      if (targetTab === 'account') {
         syncAccountStateToSolid()
      }

      activeTab = targetTab
      setActiveTabState(targetTab)
      if (!suppressHistoryUpdate) {
         const params = new URLSearchParams(window.location.search)
         params.set('tab', targetTab)
         const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`
         window.history.replaceState({ tab: targetTab }, '', newUrl)
      }
   }

   /**
    * Refreshes the current-fast UI and (re)starts the per-second timer when a fast is active.
    */
   function updateUI() {
      if (timerInterval) {
         clearInterval(timerInterval)
         timerInterval = null
      }

      syncCurrentFastStateToSolid()

      if (shouldShowStartupSyncLoading()) {
         startupSyncIndicator.show()
         return
      }
      startupSyncIndicator.clear()
      if (currentFast) {
         timerInterval = setInterval(updateTimer, 1000)
         updateTimer()
      }
   }

   /**
    * Cancels any pending debounced duration-adjust sync.
    */
   function cancelAdjustDurationSync() {
      if (adjustDurationSyncTimerId !== null) {
         window.clearTimeout(adjustDurationSyncTimerId)
         adjustDurationSyncTimerId = null
      }
   }

   /**
    * Debounces a sync after the fast duration is adjusted.
    */
   function scheduleAdjustDurationSync() {
      cancelAdjustDurationSync()
      adjustDurationSyncTimerId = window.setTimeout(() => {
         adjustDurationSyncTimerId = null
         syncState('adjust-duration')
      }, ADJUST_DURATION_SYNC_DEBOUNCE_MS)
   }

   /**
    * Routes a current-fast change to the appropriate remote sync (edit/delete/adjust/full).
    *
    * @param detail - The current-fast state change detail.
    */
   function syncCurrentFastChangeToRemote(detail: CurrentFastStateDetail) {
      if (
         !userIdentity.userId ||
         !canUseRemoteSync() ||
         typeof detail.action !== 'string'
      ) {
         return
      }
      if (
         detail.action === 'edit-fast' &&
         detail.historyChange?.type === 'edit' &&
         detail.historyChange.entry
      ) {
         const editedEntry = deserializeFastEntry(detail.historyChange.entry)
         if (editedEntry) {
            historyApi.upsertRemoteHistoryEntry(editedEntry).catch(error => {
               console.error('Failed to sync edited history entry', error)
            })
         }
         return
      }
      if (
         detail.action === 'delete-fast' &&
         detail.historyChange?.type === 'delete' &&
         typeof detail.historyChange.historyId === 'number'
      ) {
         historyApi
            .deleteRemoteHistoryEntry(detail.historyChange.historyId)
            .catch(error => {
               console.error('Failed to delete remote history entry', error)
            })
         return
      }
      if (detail.action === 'adjust-duration') {
         scheduleAdjustDurationSync()
         return
      }
      syncState(detail.action).catch(error => {
         console.error('Failed to sync Solid current fast update', error)
      })
   }

   /**
    * Per-second tick: recomputes remaining time, updates the summary, and fires the completion notification.
    */
   function updateTimer() {
      if (!currentFast) return

      const now = Date.now()
      const remaining = currentFast.duration - (now - currentFast.startTime)
      historySummaryController.update()

      if (remaining <= 0) {
         triggerFastCompleteNotification()
      }
   }

   /**
    * Applies the timer display-mode preference, optionally persisting and updating the active fast.
    *
    * @param mode - The display mode to apply.
    * @param options - Whether to persist locally and update the current fast.
    */
   function applyDisplayModePreference(
      mode: unknown,
      options: { persistLocal?: boolean; updateCurrentFast?: boolean } = {},
   ) {
      const persistLocal = options.persistLocal !== false
      const updateCurrentFast = options.updateCurrentFast !== false
      const normalized = normalizeDisplayModePreference(mode) ?? DISPLAY_MODE_ELAPSED
      displayModePreference = normalized
      showTimeRemaining = normalized === DISPLAY_MODE_REMAINING
      if (updateCurrentFast && currentFast) {
         currentFast.displayMode = normalized
      }
      if (persistLocal) {
         persistDisplayMode(normalized)
      }
      syncCurrentFastStateToSolid()
   }

   /**
    * Returns the current timer display-mode preference.
    *
    * @returns The display mode.
    */
   function getDisplayModePreference() {
      return displayModePreference
   }

   // --- Initialize ---
   initializeApp()

   return {
      /**
       * Handles an account action from the Solid UI (create/enter key, QR, download, delete, save).
       *
       * @param detail - The account action detail.
       */
      handleAccountAction: detail => routeAccountAction(accountController, detail),
      /**
       * Handles an app-level action (retry sync, locale, history period, freeze summary).
       *
       * @param detail - The app action detail.
       */
      handleAppAction: detail =>
         routeAppAction(
            {
               syncEngine,
               historySummaryController,
               applyLocalePreference,
               applyUnitSystemPreference,
            },
            detail,
         ),
      /**
       * Handles a tab-change request from the Solid UI.
       *
       * @param detail - The tab-change detail.
       */
      handleActiveTabChange(detail) {
         switchTab(detail?.tab, { suppressHistoryUpdate: true })
      },
      /**
       * Applies a current-fast/history change from the Solid store and syncs it remotely.
       *
       * @param detail - The current-fast state detail.
       */
      handleCurrentFastStateChange(detail) {
         currentFast = deserializeFastEntry(detail.currentFast) ?? null
         if (currentFast) {
            latestLocalDurationChangeAt = Math.max(
               latestLocalDurationChangeAt,
               currentFast.durationUpdatedAt,
            )
         }
         const displayMode = normalizeDisplayModePreference(detail.displayMode)
         if (displayMode) {
            applyDisplayModePreference(displayMode, {
               persistLocal: false,
            })
         }
         fastHistory = parseFastHistoryPayload(detail.fastHistory)
         sortFastHistoryDescending()
         historySummaryController.clearCache()
         syncCurrentFastStateToSolid()
         syncHistoryStateToSolid()
         historySummaryController.update({ force: true })
         syncCurrentFastChangeToRemote(detail)
      },
      /**
       * Handles a change in displayed history months, loading more remote history as needed.
       *
       * @param detail - The history-months detail.
       */
      handleHistoryMonthsChange(detail) {
         historyMonthsDisplayed =
            typeof detail?.months === 'number' ?
               Math.max(1, detail.months)
            :  solidHistoryMonthsDisplayed()
         syncHistoryStateToSolid()
         if (userIdentity.userId && canUseRemoteSync()) {
            syncHistoryRemoteStateToSolid({
               loading: true,
               errorMessage: '',
            })
            remoteSyncRuntime
               .ensureRemoteHistoryForMonths(historyMonthsDisplayed)
               .then(() => {
                  syncHistoryRemoteStateToSolid({
                     loading: false,
                     errorMessage: '',
                  })
                  syncHistoryStateToSolid()
               })
               .catch(error => {
                  console.error('Failed to extend remote history range', error)
                  syncHistoryRemoteStateToSolid({
                     loading: false,
                     errorMessage:
                        'Unable to load remote history. Showing saved entries.',
                  })
                  syncHistoryStateToSolid()
               })
         }
      },
      /**
       * Applies a habit change from the Solid store, persisting and queuing a remote sync.
       *
       * @param detail - The habit state detail.
       */
      handleHabitStateChange(detail) {
         const normalizedDetail = detail
         habits = normalizeHabitsForStorage(normalizedDetail.habits)
         markStateChanged()
         saveState()
         syncHabitsStateToSolid()
         habitsSync.recordLocalChange()
      },
      /**
       * Applies a weight change from the Solid store, persisting preferences and syncing.
       *
       * @param detail - The weight state detail.
       */
      handleWeightStateChange(detail) {
         const normalizedDetail = detail
         weightHistory = parseWeightHistoryPayload(normalizedDetail.weightHistory)
         weightsSync.recordLocalChange()
         weightFilter =
            (
               typeof normalizedDetail.weightFilter === 'string' &&
               Object.prototype.hasOwnProperty.call(
                  WEIGHT_FILTER_CONFIG,
                  normalizedDetail.weightFilter,
               )
            ) ?
               normalizedDetail.weightFilter
            :  solidWeightFilter()
         if (typeof normalizedDetail.showWeightTarget === 'boolean') {
            showWeightTarget = normalizedDetail.showWeightTarget
            persistShowWeightTarget(showWeightTarget)
         }
         if (
            normalizedDetail.chartMode === 'line' ||
            normalizedDetail.chartMode === 'scatter'
         ) {
            persistWeightChartMode(normalizedDetail.chartMode)
         }
         markStateChanged()
         saveState()
         if (
            userIdentity.userId &&
            canUseRemoteSync() &&
            normalizedDetail.action === 'update-weight-chart-mode'
         ) {
            syncState(normalizedDetail.action).catch(error => {
               console.error('Failed to sync Solid weight update', error)
            })
         }
      },
      /**
       * Schedules a debounced push after a local measurements change.
       */
      handleMeasurementsStateChange() {
         measurementsSync.schedulePush()
      },
      /**
       * Handles window focus by requesting a remote refresh.
       */
      handleFocus() {
         requestRemoteRefreshOnFocus()
      },
      /**
       * Handles wide-screen breakpoint changes and adjusts the tab menu.
       *
       * @param matches - Whether the wide-screen breakpoint matches.
       */
      handleWideScreenViewportChange(matches) {
         isWideScreenViewport = matches === true
         syncTabMenuForViewport()
      },
   }
}
